// @ai-generated 2026-09-01T12:00:00Z by matthias.gessinger@synqony.com using Claude Opus 5
import { useCallback, useRef, useState } from 'react';

export interface IdLookup<TValue> {
    /** What has been loaded so far. An id whose value has not arrived is absent. */
    valuesById: ReadonlyMap<string, TValue>;
    /** Loads the values of the given ids, skipping every id already asked for. */
    request: (ids: readonly (string | undefined)[]) => void;
}

/**
 * A map filled on demand: the caller names ids, the loader answers with values, and each id is asked
 * for at most once for as long as the hook lives.
 *
 * For records that are looked up by id because a linked object carries only that id, and that do not
 * change while the frame is open. `load` receives only the ids not asked for before and must be
 * stable (a `useCallback`), as must `onLoadFailed`, because `request` is only as stable as they are.
 */
export function useIdLookup<TValue>(
    load: (ids: string[]) => Promise<Iterable<readonly [string, TValue]>>,
    onLoadFailed?: (error: unknown) => void,
): IdLookup<TValue> {
    const [valuesById, setValuesById] = useState<ReadonlyMap<string, TValue>>(new Map());
    const requestedIds = useRef<Set<string>>(new Set());

    const request = useCallback(
        (ids: readonly (string | undefined)[]) => {
            const missing = [...new Set(ids)].filter(
                (id): id is string => id !== undefined && id !== '' && !requestedIds.current.has(id),
            );
            if (missing.length === 0) {
                return;
            }

            // Marked before the call, and in a ref rather than in state, so neither a re-render nor
            // React's double-invoked effects ask for the same ids again while the first call is still
            // out. An id the loader answers nothing for stays marked: it is absent from the map, and
            // asking again would only produce the same silence on every render.
            missing.forEach((id) => requestedIds.current.add(id));

            void (async () => {
                try {
                    const loaded = await load(missing);

                    setValuesById((previous) => {
                        const merged = new Map(previous);
                        for (const [id, value] of loaded) {
                            merged.set(id, value);
                        }
                        return merged;
                    });
                } catch (error) {
                    // A failed call is worth retrying, unlike an answered one, so the marks come back off.
                    missing.forEach((id) => requestedIds.current.delete(id));
                    onLoadFailed?.(error);
                }
            })();
        },
        [load, onLoadFailed],
    );

    return { valuesById, request };
}
